import React, { ChangeEvent, ReactElement, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import {
    clearGoodsManipulationItem,
    createNewSellGoodsManipulationItem,
    sellGoods,
    setGoodsManipulationItemClientId,
    setGoodsManipulationItemExecutionTime,
    setGoodsManipulationItemQuantity,
    setGoodsManipulationItemReason,
    setGoodsManipulationItemSellingPrice,
} from '../../../redux/actions/goodsActions';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { Typography, DialogContent, Grid2, TextField, InputAdornment } from '@mui/material';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { DimensionTypeDimensionSR, DimensionTypeSR } from '../../../common/Types';
import { useParams } from 'react-router-dom';
import { getCustomers } from '../../../redux/selectors/clientsSelector';
import { useSelector } from 'react-redux';
import ClientAutocomplete from '../../../common/components/autocomplete/ClientAutocomplete';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type SellGoodsFormProps = {
    articleId?: number;
    open: boolean;
    closeForm(): void;
    goodsQuantity: number;
};

export default function SellGoodsForm(props: SellGoodsFormProps): ReactElement {
    const { articleId, open, closeForm, goodsQuantity } = props;

    const articles = useAppSelector((state: RootState) => state.articles.articles);
    const clients = useSelector(getCustomers);

    const goodsManipulationItem = useAppSelector(
        (state: RootState) => state.goodsStatusManipulation.goodsManipulationItem,
    );
    const saveStatus = useAppSelector((state: RootState) => state.goodsStatusManipulation.saveStatus);

    const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);

    const dispatch = useAppDispatch();

    const params = useParams();

    useEffect(() => {
        const siteId = params.siteId;
        if (
            !goodsManipulationItem &&
            open &&
            !!siteId &&
            !isNaN(+siteId) &&
            +siteId > 0 &&
            !!articleId &&
            articleId > 0
        ) {
            dispatch(createNewSellGoodsManipulationItem({ siteId: +siteId, articleId }));
        }
    }, [dispatch, open, params, articleId]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleClose = () => {
        dispatch(clearGoodsManipulationItem());
        closeForm();
    };

    const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;

        dispatch(setGoodsManipulationItemReason(value));
    };

    const handleExecutionTimeChange = (date: Dayjs | null) => {
        if (!!date) {
            const newDate = date.toDate();

            dispatch(setGoodsManipulationItemExecutionTime(newDate));
        }
    };

    const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const type = event.target.type;

        if (!isNaN(+value) && type === 'number') {
            dispatch(setGoodsManipulationItemQuantity(+value));
        }
    };

    const handleSelectClient = (clientId?: number) => {
        dispatch(setGoodsManipulationItemClientId(clientId));
    };

    const handleSellingPriceChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const type = event.target.type;

        if (!isNaN(+value) && type === 'number') {
            dispatch(setGoodsManipulationItemSellingPrice(+value));
        }
    };

    const handleSave = () => {
        if (isEverythingOk) {
            dispatch(sellGoods());
        }
    };

    const article = articles?.find((a) => a.id === articleId);

    const articleName = useMemo(() => {
        if (!!article) {
            return article?.title
                ? article.manufacturer
                    ? article.title + ', ' + article.manufacturer
                    : article.title
                : (article.manufacturer ?? '/');
        }
        return '/';
    }, [article]);

    const isThereEnoughItems =
        !goodsManipulationItem || !goodsManipulationItem.quantity || goodsManipulationItem.quantity <= goodsQuantity;

    const isEverythingOk =
        !!goodsManipulationItem &&
        !!goodsManipulationItem?.quantity &&
        goodsManipulationItem?.quantity > 0 &&
        !!goodsManipulationItem?.executionTime &&
        !!goodsManipulationItem?.sellingPrice &&
        goodsManipulationItem?.sellingPrice > 0;

    return (
        <ResponsiveDialog maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>Prodaj robu - {articleName}</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: 1000, margin: 'auto' }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={
                                !!goodsManipulationItem?.quantity && goodsManipulationItem.quantity > 0 ? false : true
                            }
                            required
                            fullWidth
                            name="quantity"
                            label={article?.dimensionType ? DimensionTypeSR[article.dimensionType] : 'Količina'}
                            type="number"
                            slotProps={{
                                htmlInput: { inputMode: 'decimal' },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {goodsQuantity ?? '/'} (
                                            {article?.dimensionType
                                                ? DimensionTypeDimensionSR[article.dimensionType]
                                                : 'kom.'}
                                            )
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            value={!!goodsManipulationItem?.quantity ? goodsManipulationItem?.quantity : ''}
                            variant="outlined"
                            onChange={handleQuantityChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={
                                !!goodsManipulationItem?.sellingPrice && goodsManipulationItem.sellingPrice > 0
                                    ? false
                                    : true
                            }
                            required
                            fullWidth
                            name="sellingPrice"
                            label="Prodajna cena"
                            type="number"
                            slotProps={{
                                htmlInput: { inputMode: 'decimal' },
                                input: {
                                    endAdornment: companyDetails?.currency ? (
                                        <InputAdornment position="end">({companyDetails.currency})</InputAdornment>
                                    ) : (
                                        <></>
                                    ),
                                },
                            }}
                            value={!!goodsManipulationItem?.sellingPrice ? goodsManipulationItem?.sellingPrice : ''}
                            variant="outlined"
                            onChange={handleSellingPriceChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Vreme izvršenja"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={
                                    !!goodsManipulationItem?.executionTime
                                        ? dayjs(goodsManipulationItem?.executionTime)
                                        : null
                                }
                                onChange={(value) => handleExecutionTimeChange(value)}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <div>
                            <ClientAutocomplete
                                title="Prodato klijentu"
                                clients={clients ?? []}
                                selectClient={handleSelectClient}
                                selectedClientId={goodsManipulationItem?.soldToClientId}
                            />
                        </div>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            name="reason"
                            label="Razlog"
                            multiline
                            rows={6}
                            value={goodsManipulationItem?.reason ?? ''}
                            variant="outlined"
                            onChange={handleReasonChange}
                        />
                    </Grid2>
                    {!isEverythingOk && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*unesite sva obavezna polja</Typography>
                        </Grid2>
                    )}
                    {!isThereEnoughItems && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">
                                *nema dovoljno robe za zahtevani nalog. Trenutno raspolažete sa{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {goodsQuantity ?? '/'}{' '}
                                    {article?.dimensionType ? DimensionTypeDimensionSR[article.dimensionType] : 'kom.'}
                                </span>
                            </Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={!isEverythingOk || !isThereEnoughItems || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
